/**
 * Attribution Tracking
 *
 * Captures UTM params and referrer on first visit, persists across redirects.
 * Uses in-memory caching to avoid localStorage reads on every event.
 */

// In-memory cache to avoid localStorage reads on every event
let cachedAttribution: Record<string, string> | null = null;

const STORAGE_KEY = 'gpt_wm_attr';
const REDIRECT_KEY = 'gpt_wm_attr_redirect';

/**
 * Capture attribution on page load. Call once in entry-client.tsx.
 */
export function captureAttribution(): void {
  if (typeof window === 'undefined') return;

  const params = new URLSearchParams(window.location.search);
  const utmSource = params.get('utm_source');
  const utmMedium = params.get('utm_medium');
  const utmCampaign = params.get('utm_campaign');
  const hasNewUTM = utmSource || utmMedium || utmCampaign;

  // If new UTM params, update attribution (campaign override)
  if (hasNewUTM) {
    const attr: Record<string, string> = {};
    if (utmSource) attr.src = utmSource;
    if (utmMedium) attr.med = utmMedium;
    if (utmCampaign) attr.cmp = utmCampaign;
    const term = params.get('utm_term');
    const content = params.get('utm_content');
    if (term) attr.trm = term;
    if (content) attr.cnt = content;

    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(attr));
      cachedAttribution = attr;
    } catch { /* ignore */ }
    return;
  }

  // First visit - capture referrer if external
  try {
    const existing = localStorage.getItem(STORAGE_KEY);
    if (!existing && document.referrer) {
      const refDomain = new URL(document.referrer).hostname;
      if (refDomain !== window.location.hostname) {
        const attr = { ref: refDomain };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(attr));
        cachedAttribution = attr;
      }
    }
  } catch { /* ignore */ }
}

/**
 * Get attribution for Rybbit events. Cached in memory after first read.
 */
export function getAttribution(): Record<string, string> {
  if (cachedAttribution) return cachedAttribution;
  if (typeof window === 'undefined') return {};

  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    cachedAttribution = stored ? JSON.parse(stored) : {};
    return cachedAttribution;
  } catch {
    return {};
  }
}

/**
 * Save state before redirect (auth/Stripe). Restores automatically.
 */
export function saveBeforeRedirect(): void {
  if (typeof window === 'undefined') return;
  try {
    const attr = getAttribution();
    if (Object.keys(attr).length > 0) {
      sessionStorage.setItem(REDIRECT_KEY, JSON.stringify(attr));
    }
  } catch { /* ignore */ }
}

/**
 * Restore state after redirect. Call on callback pages.
 */
export function restoreAfterRedirect(): void {
  if (typeof window === 'undefined') return;
  try {
    const stored = sessionStorage.getItem(REDIRECT_KEY);
    if (stored) {
      localStorage.setItem(STORAGE_KEY, stored);
      cachedAttribution = JSON.parse(stored);
      sessionStorage.removeItem(REDIRECT_KEY);
    }
  } catch { /* ignore */ }
}
